import { useState } from 'react';
import { useApi } from '../lib/hooks.js';
import { ApiError, apiRequest } from '../lib/api.js';
import { useAuth } from '../state/auth.js';
import { dateTime, relativeTime } from '../lib/format.js';
import { Alert, Badge, Button, Card, Field, TextInput } from '../components/ui.js';

interface SessionRow {
  id: string;
  current: boolean;
  userAgent: string;
  ip: string;
  createdAt: string;
  lastUsedAt: string;
  expiresAt: string;
}

/**
 * Account settings.
 *
 * The session list is the visible half of a feature that is otherwise invisible:
 * access tokens are short-lived and refresh tokens rotate, so "sign out this
 * device" is a real capability rather than a UI gesture. Showing the user agent
 * and last-used time is what makes it useful — a session they do not recognise is
 * the signal that something is wrong.
 */
export function Account() {
  const { user, signOutEverywhere } = useAuth();
  const { data, loading, refetch } = useApi<{ sessions: SessionRow[] }>('/api/auth/sessions');
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function revoke(sessionId: string) {
    setBusy(sessionId);
    setError(null);
    try {
      await apiRequest(`/api/auth/sessions/${sessionId}`, { method: 'DELETE' });
      refetch();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.problem.detail : 'Could not revoke that session');
    } finally {
      setBusy(null);
    }
  }

  async function revokeAll() {
    setBusy('all');
    try {
      await signOutEverywhere();
    } finally {
      setBusy(null);
    }
  }

  if (!user) return null;

  return (
    <div className="mx-auto max-w-3xl px-4 py-12 sm:px-6">
      <h1 className="text-2xl font-semibold tracking-tight">Account</h1>

      <Card className="mt-6 p-6">
        <h2 className="text-sm font-semibold">Profile</h2>
        <div className="mt-4 grid gap-4 sm:grid-cols-2">
          <Field label="Name" htmlFor="profile-name">
            <TextInput id="profile-name" value={user.name} readOnly disabled />
          </Field>
          <Field label="Email" htmlFor="profile-email">
            <TextInput id="profile-email" value={user.email} readOnly disabled />
          </Field>
        </div>
        <p className="mt-4 text-xs text-subtle">
          Role: <span className="font-medium text-muted">{user.role}</span>
          {user.role !== 'customer' && ' — this account can reach the admin console.'}
        </p>
      </Card>

      <Card className="mt-6 overflow-hidden">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-line px-6 py-4">
          <div>
            <h2 className="text-sm font-semibold">Active sessions</h2>
            <p className="mt-0.5 text-xs text-subtle">
              Refresh tokens rotate on every use. Revoking a session takes effect immediately, even
              though its access token has not expired.
            </p>
          </div>
          <Button variant="danger" size="sm" loading={busy === 'all'} onClick={() => void revokeAll()}>
            Sign out everywhere
          </Button>
        </div>

        {error && (
          <div className="border-b border-line p-4">
            <Alert tone="danger" title="Could not revoke session" onDismiss={() => setError(null)}>
              {error}
            </Alert>
          </div>
        )}

        {loading && !data ? (
          <div className="space-y-3 p-6">
            <div className="skeleton h-12 w-full rounded-lg" />
            <div className="skeleton h-12 w-full rounded-lg" />
          </div>
        ) : (
          <ul className="divide-y divide-line">
            {(data?.sessions ?? []).map((session) => (
              <li key={session.id} className="flex flex-wrap items-center justify-between gap-3 px-6 py-4">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="truncate text-sm font-medium">{describeAgent(session.userAgent)}</p>
                    {session.current && <Badge tone="positive">This device</Badge>}
                  </div>
                  <p className="tabular mt-0.5 text-xs text-subtle">
                    {session.ip} · last used {relativeTime(session.lastUsedAt)} · started{' '}
                    {dateTime(session.createdAt)}
                  </p>
                </div>
                {!session.current && (
                  <Button
                    variant="secondary"
                    size="sm"
                    loading={busy === session.id}
                    onClick={() => void revoke(session.id)}
                  >
                    Revoke
                  </Button>
                )}
              </li>
            ))}
          </ul>
        )}
      </Card>
    </div>
  );
}

/**
 * A user agent string is 120 characters of noise. Reducing it to "Chrome on
 * macOS" is the difference between a list a person can scan and a list they
 * ignore.
 */
function describeAgent(userAgent: string): string {
  if (!userAgent || userAgent === 'unknown') return 'Unknown client';

  const browser =
    /Edg\//.test(userAgent) ? 'Edge'
    : /OPR\//.test(userAgent) ? 'Opera'
    : /Firefox\//.test(userAgent) ? 'Firefox'
    : /Chrome\//.test(userAgent) ? 'Chrome'
    : /Safari\//.test(userAgent) ? 'Safari'
    : /curl\//.test(userAgent) ? 'curl'
    : /node/i.test(userAgent) ? 'Node'
    : 'Unknown browser';

  const platform =
    /Windows/.test(userAgent) ? 'Windows'
    : /Macintosh|Mac OS X/.test(userAgent) ? 'macOS'
    : /Android/.test(userAgent) ? 'Android'
    : /iPhone|iPad/.test(userAgent) ? 'iOS'
    : /Linux/.test(userAgent) ? 'Linux'
    : null;

  return platform ? `${browser} on ${platform}` : browser;
}
